import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { collection, getDocs, query, orderBy } from 'firebase/firestore';
import { db } from '../firebase';
import ListingItem from '../components/ListingItem';
import { FaSearch, FaTimes } from 'react-icons/fa';
import Spinner from '../components/Spinner';
import { toast } from 'react-toastify';

export default function Search() {
    const [listings, setListings] = useState(null);
    const [loading, setLoading] = useState(true);
    const [searchInput, setSearchInput] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [searchFilters, setSearchFilters] = useState({
        category: '',
        subCategory: ''
    });
    const navigate = useNavigate();
    const location = useLocation();

    // Categories and Subcategories for Digital Products Marketplace
    const categories = {
        'Mobile Apps': [
            'Social network app', 'Art & Design', 'Auto & Vehicles', 'Beauty', 'Books & Reference',
            'Business', 'Comics', 'Communication', 'Dating', 'Education', 'Entertainment',
            'Events', 'Finance', 'Food & Drink', 'Health & Fitness', 'House & Home',
            'Libraries & Demo', 'Lifestyle', 'Maps & Navigation', 'Medical', 'Music & Audio',
            'News & Magazines', 'Parenting', 'Personalization', 'Photography', 'Productivity',
            'Shopping', 'Social', 'Sports', 'Tools', 'Travel & Local', 'Video Players',
            'Weather', 'Games', 'Multivendor', 'Admin Dashboard', 'Clones', 'Streaming',
            'SaaS', 'Landing Pages'
        ],
        'Web Apps': [
            'Social network app', 'Art & Design', 'Auto & Vehicles', 'Beauty', 'Books & Reference',
            'Business', 'Comics', 'Communication', 'Dating', 'Education', 'Entertainment',
            'Events', 'Finance', 'Food & Drink', 'Health & Fitness', 'House & Home',
            'Libraries & Demo', 'Lifestyle', 'Maps & Navigation', 'Medical', 'Music & Audio',
            'News & Magazines', 'Parenting', 'Personalization', 'Photography', 'Productivity',
            'Shopping', 'Social', 'Sports', 'Tools', 'Travel & Local', 'Video Players',
            'Weather', 'Games', 'Multivendor', 'Admin Dashboard', 'Clones', 'Streaming',
            'SaaS', 'Landing Pages'
        ],
        'Brand Startups': [
            'Social network app', 'Art & Design', 'Auto & Vehicles', 'Beauty', 'Books & Reference',
            'Business', 'Comics', 'Communication', 'Dating', 'Education', 'Entertainment',
            'Events', 'Finance', 'Food & Drink', 'Health & Fitness', 'House & Home',
            'Libraries & Demo', 'Lifestyle', 'Maps & Navigation', 'Medical', 'Music & Audio',
            'News & Magazines', 'Parenting', 'Personalization', 'Photography', 'Productivity',
            'Shopping', 'Social', 'Sports', 'Tools', 'Travel & Local', 'Video Players',
            'Weather', 'Games', 'Multivendor', 'Admin Dashboard', 'Clones', 'Streaming',
            'SaaS', 'Landing Pages'
        ],
        'UI/UX Kits': [
            'Flutter UI Kits', 'React UI Kits', 'Web UI Kits', 'Mobile UI Kits',
            'Admin UI Kits', 'Dashboard UI Kits'
        ],
        'Backend Systems': [
            'Firebase Backend', 'Node.js Backend', 'Django Backend', 'Laravel Backend',
            'Authentication Systems', 'API Systems'
        ],
        'AI & Automation Tools': [
            'AI Chatbots', 'AI SaaS Tools', 'Automation Scripts', 'GPT Integrations',
            'AI Image Generators'
        ],
        'Programming Courses': [
            'Flutter Development', 'React Native Development', 'Android Development',
            'iOS Development', 'JavaScript', 'React / Next.js', 'HTML & CSS',
            'Tailwind CSS', 'Node.js', 'Python', 'Django', 'Laravel', 'Firebase',
            'AI Development', 'Machine Learning', 'API Development', 'Full Stack Development',
            'DevOps'
        ],
        'Graphic designs': [
            'Drawing & Illustration', 'Vector Graphics', 'Photo Editing & Filters',
            'Layout & Desktop Publishing', 'UI & Web Design', 'Templates & Mockups',
            'Logo Makers', 'Typography & Font Tools', 'Color Tools', 'AI Design Generators',
            'Asset Libraries'
        ]
    };

    // Dynamic subcategories based on selected category
    const [subCategoryOptions, setSubCategoryOptions] = useState([]);
    useEffect(() => {
        if (searchFilters.category) {
            setSubCategoryOptions(categories[searchFilters.category] || []);
            setSearchFilters(prev => ({
                ...prev,
                subCategory: ''
            }));
        } else {
            setSubCategoryOptions([]);
        }
    }, [searchFilters.category]);

    // Extract search query from URL (for desktop header search)
    useEffect(() => {
        const queryParams = new URLSearchParams(location.search);
        const query = queryParams.get('q') || '';
        setSearchInput(query);
    }, [location.search]);

    // Fetch listings on mount
    useEffect(() => {
        async function fetchData() {
            try {
                // Fetch Listings
                const listingsRef = collection(db, "listings");
                const q = query(listingsRef, orderBy("timestamp", "desc"));
                const querySnap = await getDocs(q);
                const listingsData = querySnap.docs.map(doc => ({
                    id: doc.id,
                    ...doc.data(),
                }));
                setListings(listingsData);
                setSearchResults(listingsData);

                setLoading(false);
            } catch (error) {
                console.log(error);
                toast.error("Could not fetch data");
                setLoading(false);
            }
        }
        fetchData();
    }, []);

    // Update search results when search input or filters change
    useEffect(() => {
        if (listings) {
            handleSearch();
        }
    }, [searchInput, searchFilters, listings]);

    const handleSearch = async () => {
        if (!listings) return;

        let results = [...listings];

        // Text search by title
        if (searchInput) {
            results = results.filter(listing =>
                listing.title.toLowerCase().includes(searchInput.toLowerCase())
            );
        }

        // Apply category filter
        if (searchFilters.category) {
            results = results.filter(listing =>
                listing.category?.toLowerCase() === searchFilters.category.toLowerCase()
            );
        }

        // Apply sub-category filter
        if (searchFilters.subCategory) {
            results = results.filter(listing =>
                listing.subcategory?.toLowerCase() === searchFilters.subCategory.toLowerCase()
            );
        }

        setSearchResults(results);
    };

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setSearchFilters(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const resetFilters = () => {
        setSearchFilters({
            category: '',
            subCategory: ''
        });
        setSearchInput('');
        navigate('/search'); // Clear the URL query parameter
        if (listings) {
            setSearchResults(listings);
        }
    };

    const handleSearchInputChange = (e) => {
        const value = e.target.value;
        setSearchInput(value);
        navigate(`/search?q=${encodeURIComponent(value)}`); // Update URL for desktop header
    };

    if (loading) {
        return <Spinner />;
    }

    return (
        <div className="min-h-screen bg-black text-white py-8 px-4">
            <div className="max-w-6xl mx-auto">
                {/* Search Input - Mobile Only */}
                <div className="md:hidden mb-8">
                    <div className="relative max-w-2xl mx-auto">
                        <input
                            type="text"
                            placeholder="Search SourceCodey Listings..."
                            className="w-full p-4 pr-12 bg-gray-800 border border-gray-600 rounded-lg focus:ring-2 focus:ring-[#4da3ff] focus:border-[#4da3ff] text-white placeholder-gray-400"
                            value={searchInput}
                            onChange={handleSearchInputChange}
                        />
                        {searchInput ? (
                            <button
                                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-[#4da3ff]"
                                onClick={() => {
                                    setSearchInput('');
                                    navigate('/search');
                                }}
                            >
                                <FaTimes className="text-xl" />
                            </button>
                        ) : (
                            <button
                                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-[#4da3ff]"
                            >
                                <FaSearch className="text-xl" />
                            </button>
                        )}
                    </div>
                </div>

                {/* Filters Section */}
                <div className={`mt-6 p-6 bg-gray-800 border rounded-lg max-w-2xl mx-auto ${searchFilters.category || searchFilters.subCategory ? 'border-[#4da3ff]' : 'border-gray-600'}`}>
                    <h3 className="text-xl font-semibold text-white mb-4">Filter Digital Products</h3>
                    <div className="grid grid-cols-1 gap-6">
                        <div>
                            <label className="block mb-2 font-medium text-gray-400">Category</label>
                            <select
                                name="category"
                                value={searchFilters.category}
                                onChange={handleFilterChange}
                                className="w-full p-3 bg-gray-800 border border-gray-600 rounded-lg focus:ring-2 focus:ring-[#4da3ff] focus:border-[#4da3ff] text-white"
                            >
                                <option value="">All Categories</option>
                                {Object.keys(categories).map((category) => (
                                    <option key={category} value={category}>
                                        {category}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div>
                            <label className="block mb-2 font-medium text-gray-400">Subcategory</label>
                            <select
                                name="subCategory"
                                value={searchFilters.subCategory}
                                onChange={handleFilterChange}
                                className="w-full p-3 bg-gray-800 border border-gray-600 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-pink-500 text-white"
                                disabled={!searchFilters.category}
                            >
                                <option value="">All Subcategories</option>
                                {subCategoryOptions.map((subCategory) => (
                                    <option key={subCategory} value={subCategory}>
                                        {subCategory}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>

                    <div className="flex justify-end gap-4 mt-6">
                        <button
                            onClick={resetFilters}
                            className="px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-600 transition"
                        >
                            Reset Filters
                        </button>
                    </div>
                </div>

                {/* Results Section */}
                <div className="mt-8">
                    <h2 className="text-2xl font-semibold mb-6 text-white">
                        {searchResults.length} {searchResults.length === 1 ? 'Product' : 'Products'} Found
                    </h2>
                    
                    {searchResults.length > 0 ? (
                        <div className="movie-masonry">
                            {searchResults.map(listing => (
                                <div key={listing.id} className="masonry-item">
                                    <ListingItem listing={listing} />
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="flex flex-col items-center justify-center py-16">
                            <div className="text-center">
                                <FaSearch className="text-5xl text-gray-600 mx-auto mb-4" />
                                <p className="text-gray-400 text-lg">No digital products found</p>
                                <p className="text-gray-500 text-sm mt-2">Try adjusting your search or filters</p>
                                {(searchInput || searchFilters.category || searchFilters.subCategory) && (
                                    <button
                                        onClick={resetFilters}
                                        className="mt-4 px-6 py-2 bg-[#4da3ff] text-white rounded-lg hover:bg-blue-600 transition"
                                    >
                                        Clear All Filters
                                    </button>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Inject masonry styles */}
            <style>
                {`
                    .movie-masonry {
                        column-count: 2;
                        column-gap: 6px;
                        padding: 0 4px;
                    }

                    @media (min-width: 640px) {
                        .movie-masonry {
                            column-count: 3;
                        }
                    }

                    @media (min-width: 1024px) {
                        .movie-masonry {
                            column-count: 4;
                        }
                    }

                    .masonry-item {
                        break-inside: avoid;
                        margin-bottom: 6px;
                    }
                `}
            </style>
        </div>
    );
}